import { el, setChildren, setAttr } from 'redom';

import application from 'application';

import {
  stringToSVG,
  bytesToSize
} from 'utilities';

import IconButton from 'generics/components/icon-button';
import CircleProgressBar from 'generics/components/circle-progress-bar';
import pdfFileIconString from 'assets/images/pdf-file-icon.svg?raw';
import textFileIconString from 'assets/images/text-file-icon.svg?raw';
import wordFileIconString from 'assets/images/word-file-icon.svg?raw';
import csvFileIconString from 'assets/images/csv-file-icon.svg?raw';
import spreadsheetFileIconString from 'assets/images/spreadsheet-file-icon.svg?raw';
import codeFileIconString from 'assets/images/code-file-icon.svg?raw';
import archiveFileIconString from 'assets/images/archive-file-icon.svg?raw';
import unknownFileIconString from 'assets/images/unknown-file-icon.svg?raw';

import internalEventCodes from 'event-system/codes/internal';
import fileTypes from 'constants/file-types';

const textFileIcon = stringToSVG(textFileIconString);
const csvFileIcon = stringToSVG(csvFileIconString);
const wordFileIcon = stringToSVG(wordFileIconString);
const pdfFileIcon = stringToSVG(pdfFileIconString);
const spreadsheetFileIcon = stringToSVG(spreadsheetFileIconString);
const codeFileIcon = stringToSVG(codeFileIconString);
const archiveFileIcon = stringToSVG(archiveFileIconString);
const unknownFileIcon = stringToSVG(unknownFileIconString);

textFileIcon.classList.add('text-file-icon');
csvFileIcon.classList.add('csv-file-icon');
wordFileIcon.classList.add('word-file-icon');
pdfFileIcon.classList.add('pdf-file-icon');
spreadsheetFileIcon.classList.add('spreadsheet-file-icon');
codeFileIcon.classList.add('code-file-icon');
archiveFileIcon.classList.add('archive-file-icon');
unknownFileIcon.classList.add('unknown-file-icon');

const spreadsheetMimetypes = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet'
];

const codeMimetypes = [
  'text/html',
  'text/css',
  'text/javascript',
  'application/javascript',
  'application/json',
  'text/xml',
  'application/xml'
];

const archiveMimetypes = [
  'application/zip',
  'application/x-zip-compressed',
  'application/x-rar-compressed',
  'application/vnd.rar',
  'application/x-7z-compressed',
  'application/x-tar',
  'application/gzip'
];

class AttachmentItem {

  constructor(panel) {

    this.panel = panel;
    this.index = null;
    this.attachment = null;
    this.uploadedPercent = 0;

    this.name = el('span.name.ellipsis');
    this.size = el('span.size.sub-text');
    const nbsp = document.createElement('span');
    nbsp.innerHTML = '&nbsp;';
    this.nameAndSize = el('div.name-and-size', [
      this.name, nbsp, el('span', ' ('), this.size, el('span', ')')
    ]);
    this.deleteButton = IconButton.deleteButtonSmall({
      onClick: this.handleDeleteButtonClick.bind(this)
    });
    this.progressBar = new CircleProgressBar({ progress: 0 });

    this.el = el('.message-attachment-item');

  }

  handleUploadingError(exception) {

    let message = 'Upload failed. Please try again';

    if (exception.code === 'ATTACHMENTS_SIZE_LIMIT_REACHED') {
      message = 'Attachments size limit reached. Attachments cannot exceed 9MB in total';
    }

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message,
      seconds: 5,
      type: 'error'
    });

    this.panel.deleteByIndex(this.index);
    this.panel.tryNotifyIdle();

  }

  async uploadFile() {

    this.panel.tryNotifyWorking();
    const request = await this.panel.createAttachment(this.attachment);

    request.on('progress', this.progressHandler.bind(this));

    request.on('success', this.successHandler.bind(this));

    request.on('error', this.handleUploadingError.bind(this));

  }

  successHandler(files) {

    this.panel.setByIndex(this.index, files[0]);
    this.panel.tryNotifyIdle();

  }

  progressHandler(data) {

    this.uploadedPercent = Math.floor(data.percent);

    if (this.uploadedPercent === 100) {
      this.uploadedPercent = 99;
    }

    this.progressBar.set(this.uploadedPercent);

  }

  getIcon(file) {

    const fileType = application.domUtility.determineFileType(file);

    if (fileType === fileTypes.IMAGE) {
      return el('img', { src: file.url });
    } else if (fileType === fileTypes.VIDEO) {
      return el('video', { src: file.url, controls: false, preload: 'metadata' });
    } else if (fileType === fileTypes.AUDIO) {
      return el('audio', { src: file.url, controls: false, preload: 'metadata' });
    } else {
      const mimetype = file.mimetype;

      if (mimetype === 'application/pdf') {
        return pdfFileIcon.cloneNode(true);
      }

      if (mimetype === 'text/csv') {
        return csvFileIcon.cloneNode(true);
      }

      if (mimetype === 'text/plain') {
        return textFileIcon.cloneNode(true);
      }

      if (spreadsheetMimetypes.indexOf(mimetype) !== -1) {
        return spreadsheetFileIcon.cloneNode(true);
      }

      if (codeMimetypes.indexOf(mimetype) !== -1) {
        return codeFileIcon.cloneNode(true);
      }

      if (archiveMimetypes.indexOf(mimetype) !== -1) {
        return archiveFileIcon.cloneNode(true);
      }

      const wordFileTypes = [
        'application/msword',
        'application/vnd.openxmlformats',
        'officedocument.wordprocessingml.document',
        'application/venturz-page-template'
      ];

      if (wordFileTypes.indexOf(mimetype) !== -1) {
        return wordFileIcon.cloneNode(true);
      }

      return unknownFileIcon.cloneNode(true);

    }

  }

  handleDeleteButtonClick() {
    this.panel.delete(this.attachment);
  }

  updateNameAndSize() {

    const nbsp = document.createElement('span');
    nbsp.innerHTML = '&nbsp;';

    let children = [
      this.name, nbsp, el('span', ' ('), this.size, el('span', ')')
    ];

    if (this.attachment.mimetype === 'application/venturz-page-template') {
      children = [
        this.name
      ];
    }

    setChildren(this.nameAndSize, children);

  }

  update(attachment, index) {

    this.attachment = attachment;
    this.index = index;

    this.name.textContent = attachment.name + ' ';
    setAttr(this.name, {
      title: attachment.name
    });
    this.size.textContent = bytesToSize(attachment.size);
    this.icon = el('div.icon', [this.getIcon(attachment)]);

    if (this.attachment.raw === true) {
      this.progressBar.set(0);
      setChildren(this.el, [
        this.icon, this.nameAndSize, this.progressBar
      ]);
    } else {
      setChildren(this.el, [
        this.icon, this.nameAndSize, this.deleteButton
      ]);
    }

    this.updateNameAndSize();

  }

}

export default AttachmentItem;